import {
  EAlertStatus,
  ETrainingMilestoneStatus,
  EUserStatus, EViewedUpdate,
  IAlert,
  IChainUserComment,
  IFilterUserDTO,
  IResetPasswordDTO,
  ISetupWizardUser,
  ITokenPayload,
  ITrainingMilestone,
  IUpdateResidentPasswordDTO,
  IUser,
  IUserAlertSettings,
  IUserOverdueHistory,
  IWorkStatisticsDTO,
  UserAssignment,
  UserReassignment
} from '@zunta/configurations'
import { IBaseStore } from '../types'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import Api from '@/api'

const groupBy = require('lodash.groupby')

export interface IUserState {
  users: IUser[];
  usersAll: IUser[];
  userAssignments: UserAssignment[];
  total: number;
  lastQuery: IFilterUserDTO;
  facilityUsers: IUser[];
  selectedUser: IUser | null;
  workStatistics: IWorkStatisticsDTO | null;
  comments: IChainUserComment[];
  trainingMilestones: ITrainingMilestone[];
  alerts: IAlert[];
  userAlertSettings: IUserAlertSettings[];
  adminUsers: IUser[];
  adminUsersTotal: number;
  todos: any[];
  todosTotal: number;
  todoStatistics: any[];
  dashboardData: any[];
  dashboardStatistic: any;
  todosPagination: any;
  userOverdueHistory: IUserOverdueHistory[];
  usersPagination: any;
}

export interface IUserStore extends IBaseStore {
  state: IUserState;
}

@Module({
  name: 'user',
  namespaced: true
})
export class UserModule extends VuexModule {
  user: IUser | null | ITokenPayload = null
  users: IUser[] = [];
  usersAll: IUser[] = [];
  adminUsers: IUser[] = [];
  adminUsersTotal: number = 0;
  userAssignments: UserAssignment[] = [];
  total: number = 0;
  lastQuery: IFilterUserDTO = {};
  facilityUsers: IUser[] = [];
  selectedUser: IUser | null = null;
  workStatistics: IWorkStatisticsDTO | null = null;
  comments: IChainUserComment[] = [];
  trainingMilestones: ITrainingMilestone[] = [];
  alerts: IAlert[] = [];
  userAlertSettings: IUserAlertSettings[] = []
  todos: any[] = [];
  todosTotal: number = 0;
  todoStatistics: any[] = [];
  dashboardData: any[] = [];
  dashboardStatistic: any;
  todosPagination: any;
  userOverdueHistory: IUserOverdueHistory[] = []
  usersPagination: any;

  @Mutation
  SET_USER_OVERDUE_HISTORY (data: IUserOverdueHistory[]) {
    this.userOverdueHistory = data
  }

  @Mutation
  SET_USERS_PAGINATION (pagination: any) {
    this.usersPagination = pagination
  }

  @Mutation
  SET_TODOS_PAGINATION (pagination: any) {
    this.todosPagination = pagination
  }

  @Mutation
  SET_USERS_DASHBOARD_DATA (dashboardData: any[]) {
    this.dashboardData = dashboardData
    const statistic = {
      alerts: {
        due: 0,
        overdue: 0,
        completed: 0
      },
      tasks: {
        due: 0,
        overdue: 0,
        completed: 0
      }
    }
    dashboardData.forEach((data: any) => {
      statistic.alerts.due += data.alerts.due
      statistic.alerts.overdue += data.alerts.overdue
      statistic.alerts.completed += data.alerts.completed

      statistic.tasks.due += data.tasks.due
      statistic.tasks.overdue += data.tasks.overdue
      statistic.tasks.completed += data.tasks.completed
    })
    this.dashboardStatistic = statistic
  }

  @Mutation
  SET_TODO_STATISTICS (data: any[]) {
    this.todoStatistics = data
  }

  @Mutation
  SET_TODOS_TOTAL (total: number) {
    this.todosTotal = total
  }

  @Mutation
  SET_TODOS (todos: any[]) {
    this.todos = todos
  }

  @Mutation
  SET_USER_ALERT_SETTINGS (settings: IUserAlertSettings[]) {
    this.userAlertSettings = settings
  }

  @Mutation
  SET_ALERTS (alerts: IAlert[]) {
    this.alerts = alerts
  }

  @Mutation
  SET_TRAINING_MILESTONES (milestones: ITrainingMilestone[]) {
    this.trainingMilestones = milestones
  }

  @Mutation
  SET_USER_COMMENTS (comments: IChainUserComment[]) {
    const groupedComments = []
    const grouped = groupBy(comments, 'baseComment')
    for (let i = 0; i < comments.length; i++) {
      const comment = comments[i]

      if (comment.baseComment) continue

      const commentWithReply = {
        ...comment,
        reply: grouped[comment.id] || []
      }
      groupedComments.push(commentWithReply)
    }
    this.comments = groupedComments
  }

  @Mutation
  SET_USER (user: IUser | null | ITokenPayload) {
    this.user = user
  }

  @Mutation
  SET_WORK_STATISTICS (statistics: IWorkStatisticsDTO | null) {
    this.workStatistics = statistics
  }

  @Mutation
  UPDATE_LAST_QUERY (query: IFilterUserDTO) {
    this.lastQuery = query
  }

  @Mutation
  UPDATED_SELECTED_USER (user: IUser | null) {
    this.selectedUser = user
  }

  @Mutation
  UPDATE_USERS_TOTAL (total: number) {
    this.total = total
  }

  @Mutation
  UPDATE_ADMIN_USERS_TOTAL (total: number) {
    this.adminUsersTotal = total
  }

  @Mutation
  ADD_USER (user: IUser) {
    this.users = [...this.users, user]
  }

  @Mutation
  UPDATE_USERS_LIST (users: IUser[]) {
    this.users = users
  }

  @Mutation
  UPDATE_USERS_LIST_2 (users: IUser[]) {
    this.usersAll = users
  }

  @Mutation
  UPDATE_ADMIN_USERS_LIST (users: IUser[]) {
    this.adminUsers = users
  }

  @Mutation
  RESET_FACILITY_USERS () {
    this.facilityUsers = []
  }

  @Mutation
  UPDATE_USER_STATUS (input: { id: number, status: EUserStatus }) {
    const user = this.users.find((user: IUser) => user.id === input.id)
    if (user) {
      user.status = input.status
    }
  }

  @Mutation
  UPDATE_FACILITY_USERS (facilityUsers: IUser[]) {
    this.facilityUsers = facilityUsers
  }

  @Action
  async fetchUsersPagination (filters: any) {
    const pagination = await Api.user.getPagination(filters)
    this.SET_USERS_PAGINATION(pagination)
  }

  @Action
  async fetchUserOverdueHistory (input: {
    userId: number | string,
    weeks: number,
    skip: number
  }) {
    const result = await Api.training.getUserOverdueHistory(input.userId, input.weeks, input.skip)
    this.SET_USER_OVERDUE_HISTORY(result)
  }

  @Action
  async updateChainUserComment (input: { id: number | string, text: string }) {
    await Api.user.updateChainUserComment(input.id, input.text)
    await this.fetchChaiUserComments((this.selectedUser as IUser).id)
  }

  @Action
  async removeChainUserComment (id: number | string) {
    await Api.user.removeChainUserComment(id)
    await this.fetchChaiUserComments((this.selectedUser as IUser).id)
  }

  @Action
  async fetchTodosPagination (input: { todo: number | string, query: any }) {
    const pagination = await Api.training.getTodosPagination(input.todo, input.query)
    this.SET_TODOS_PAGINATION(pagination)
  }

  @Action
  async fetchUsersDashboardData () {
    const data = await Api.training.getDashboardData()
    this.SET_USERS_DASHBOARD_DATA(data)
  }

  @Action
  async fetchTodoStatistics (query: any) {
    const data = await Api.training.getTodosStatistics(query)
    this.SET_TODO_STATISTICS(data)
  }

  @Action
  async fetchTodos (query: any) {
    const { total, todos } = await Api.training.getTodos(query)
    this.SET_TODOS(todos)
    this.SET_TODOS_TOTAL(total)
  }

  @Action
  async fetchUserAlertSettings (userId: number | string) {
    const settings = await Api.training.getUserAlertSettings(userId)
    this.SET_USER_ALERT_SETTINGS(settings)
  }

  @Action
  async updateUserAlertSettings (settings: IUserAlertSettings[]) {
    await Api.training.updateUserAlertSettings(settings)
    await this.fetchUserAlertSettings((this.selectedUser as IUser).id)
  }

  @Mutation
  SET_CHECK_USER_ASSIGNMENTS (userAssignments: UserAssignment[]) {
    this.userAssignments = userAssignments
  }

  @Action
  setUser (user: IUser | null | ITokenPayload) {
    this.SET_USER(user)
  }

  @Action
  async changeAlertStatus (input: { alertId: number | string, data: { comments: string, status: EAlertStatus, dueDate?: Date | string | number } }) {
    await Api.training.changeAlertStatus(input.alertId, input.data)
    await this.fetchAlerts((this.selectedUser as IUser).id)
  }

  @Action
  async changeChainUserCommentPin (input: { commentId: number, pin: boolean }) {
    await Api.user.changeChainUserCommentPin(input.commentId, input.pin)
  }
  @Action
  async fetchAlerts (userId: number | string) {
    const alerts = await Api.training.getUserAlerts(userId)
    this.SET_ALERTS(alerts)
  }

  @Action
  async fetchChaiUserComments (userId: number | string) {
    const comments = await Api.user.fetchChainUserComments(userId)
    this.SET_USER_COMMENTS(comments)
  }

  @Action
  async addUserComment (input: {
    userId: number | string,
    data: { text: string, baseComment: number | null }
  }) {
    await Api.user.addChainUserComment(input.userId, input.data)
  }

  @Action
  async fetchUserTrainingMilestones (userId: number | string) {
    const milestones = await Api.training.getUserTrainingMilestones(userId)
    this.SET_TRAINING_MILESTONES(milestones)
  }

  @Action
  async changeTrainingMilestoneStatus (input: { milestone: number | string, status: ETrainingMilestoneStatus }) {
    await Api.training.changeTrainingMilestoneStatus(input.milestone, input.status)
  }

  @Action
  async getOnlyAdminUsersList (query: IFilterUserDTO) {
    const { users, total } = await Api.user.getAdminUsersList(query)
    this.UPDATE_ADMIN_USERS_LIST(users || [])
    this.UPDATE_ADMIN_USERS_TOTAL(total)
  }

  @Action
  async getAdminUsersList (query: IFilterUserDTO) {
    const { users, total } = await Api.user.getAdminUsersList(query)
    this.UPDATE_LAST_QUERY(query)
    this.UPDATE_USERS_TOTAL(total)
    this.UPDATE_USERS_LIST(users || [])
  }

  @Action
  async getManagementUsersList (query: any) {
    const { users, total } = await Api.user.getManagementUsersList(query)
    this.UPDATE_LAST_QUERY(query)
    this.UPDATE_USERS_TOTAL(total)
    this.UPDATE_USERS_LIST(users || [])
  }

  @Action
  async getManagementUsersAll () {
    const { users } = await Api.user.getManagementUsersList({})
    this.UPDATE_USERS_LIST_2(users || [])
  }

  @Action
  async uploadAvatar (input: { userId: number | string, file: any }) {
    await Api.user.changeUserAvatar(input.userId, input.file)
  }

  @Action
  async createUser (input: { data: Partial<IUser>, type: string }) {
    const response = await Api.user.createUser(input.data)
    if (!response.error) {
      if (input.type === 'admin') {
        this.getAdminUsersList(this.lastQuery)
      }
      return response
    }
    return response
  }

  @Action
  async saveTrainingMilestones (input: { userId: number | string, milestones: ITrainingMilestone[] }) {
    await Api.training.saveTrainingMilestones(input.userId, input.milestones)
    await this.fetchUserTrainingMilestones(input.userId)
  }

  @Action
  async updateUser (input: {id: number, data: any, type: string }) {
    const response = await Api.user.updateUser(input.id, input.data)
    if (!response || !response.error) {
      if (input.type === 'admin') {
        this.getAdminUsersList(this.lastQuery)
      }
      return null
    }
    return response
  }

  @Action
  async checkUserAssignments (input: { userId: number, facilities?: number[] }) {
    const response = await Api.user.checkUserAssignments(input.userId, input.facilities)
    if (response) {
      this.SET_CHECK_USER_ASSIGNMENTS(response)
    }
  }

  @Action
  async makeUserReassignments (input: UserReassignment[]) {
    return Api.user.makeUserReassignments(input)
  }

  @Action
  async updateStatus (input: { id: number, status: EUserStatus }) {
    const response = await Api.user.updateStatus(input.id, input.status)
    if (!response || !response.error) {
      this.UPDATE_USER_STATUS(input)
    }
    return null
  }

  @Action
  async resetPassword (dto: IResetPasswordDTO) : Promise<string> {
    const response = await Api.user.resetPassword(dto)
    if (response.message === 'User is deactivated') {
      return 'forbidden'
    } else if (response.accessToken) {
      Api.setToken(response.accessToken, true)
      if (response.user) {
        this.context.commit('user/SET_USER', response.user as IUser, { root: true })
      }
      return 'OK'
    } else {
      return 'failed'
    }
  }

  @Action
  async fetchFacilityUsers (facilitId: number) {
    this.RESET_FACILITY_USERS()
    const facilityUsers = await Api.user.getFacilityUsers(facilitId)
    this.UPDATE_FACILITY_USERS(facilityUsers)
  }

  @Action
  async fetchUserById (id: number | string) {
    const user = await Api.user.getUserById(id)
    this.UPDATED_SELECTED_USER(user)
  }

  @Action
  async fetchChainManager (chainId: number | string) {
    const user = await Api.user.getChainManager(chainId)
    this.UPDATED_SELECTED_USER(user)
  }

  @Action
  resetSelectedUser () {
    this.UPDATED_SELECTED_USER(null)
  }

  @Action
  async fetchWorkStatistics () {
    const statistics = await Api.statistics.getWorkStatistics()
    this.SET_WORK_STATISTICS(statistics)
  }

  @Action
  async createResidentUser (data: any) {
    const response = await Api.residentPortal.createResidentUser(data)
    if (response.accessToken) {
      Api.setToken(response.accessToken, true)
      return 'OK'
    } else {
      return response
    }
  }

  @Action
  async checkOneTimeCode (code: string) {
    const checkIfNeedToFormatCode = (code: string): string => {
    const regex = /^(.{3}-.{3})$/gm
    if (regex.test(code)) {
      return code
    } else {
      const a = code.substr(0, 3)
      const b = code.substr(3, 3)
      return `${a}-${b}`.toUpperCase()
    }
  }
    return Api.residentPortal.checkCode(checkIfNeedToFormatCode(code))
  }

  @Action
  async createTempUser (code: string) {
    const response = await Api.residentPortal.createTempUser(code)
    if (response.accessToken) {
      Api.setToken(response.accessToken, true)
      return 'OK'
    } else {
      return 'failed'
    }
  }

  @Action
  async setupWizardUsers (users: ISetupWizardUser[]) {
    await Api.user.setupWizardUsers(users)
  }

  @Action
  async updateResidentUserPassword (dto: IUpdateResidentPasswordDTO) {
    return Api.residentPortal.updateResidentUserPassword(dto)
  }

  get getUser () {
    return this.user
  }

  @Action
  async setViewedVideoModule (moduleName: EViewedUpdate) {
    await Api.user.setViewedVideoModule(moduleName)
  }

  @Action
  async getViewedVideoModule (userId: string | number) {
    const res = await Api.user.getViewedVideoModule(userId)
    return res
  }

  @Action
  async getQaReportsSettings (userId: number) {
    const res = await Api.user.getQaReportsSettings(userId)
    return res
  }

  @Action
  async saveQaReportsSettings (data: any) {
    const res = await Api.user.saveQaReportsSettings(data.userId, data.reports)
    return res
  }

  @Action
  async checkResidentName (data: any) {
    const res = await Api.residentPortal.checkResidentName(data)
    return res
  }
}
